import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@core/store';
import { logout } from '@core/store/slices/authSlice';
import { setMobileMenuOpen, setCartOpen } from '@core/store/slices/uiSlice';
import { cn } from '@shared/utils';
import { useSiteSettings } from '@shared/hooks/useSiteSettings';
import { useAllCategories } from '@features/admin/categories/hooks/useCategories';
import { useAllSubCategories } from '@features/admin/subcategories/hooks/useSubCategories';

/**
 * 100% PARITY VERSION: Replicating Angular fp-navbar exactly
 */
export function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [searchExpanded, setSearchExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const [shopDropdownOpen, setShopDropdownOpen] = useState(false);
    const [mobileShopOpen, setMobileShopOpen] = useState(false);
    const [mobileCategoryOpen, setMobileCategoryOpen] = useState<string | null>(null);

    const { data: categories = [] } = useAllCategories();
    const { data: subCategories = [] } = useAllSubCategories();

    const activeCategories = useMemo(() => {
        return categories.filter(c => c.isActive);
    }, [categories]);

    const activeSubCategories = useMemo(() => {
        return subCategories.filter(s => s.isActive);
    }, [subCategories]);

    const groupedSubCategories = useMemo(() => {
        const map: Record<string, typeof activeSubCategories> = {};
        activeSubCategories.forEach(sub => {
            if (!map[sub.categoryId]) {
                map[sub.categoryId] = [];
            }
            map[sub.categoryId]!.push(sub);
        });
        return map;
    }, [activeSubCategories]);

    const { isUserAuthenticated, user } = useAppSelector((state) => state.auth);
    const { totalItems } = useAppSelector((state) => state.cart);
    const { mobileMenuOpen } = useAppSelector((state) => state.ui);


    useEffect(() => {
        // Use IntersectionObserver to handle sticky header without scroll listeners
        const sentinel = document.createElement('div');
        sentinel.style.position = 'absolute';
        sentinel.style.top = '50px';
        sentinel.style.width = '1px';
        sentinel.style.height = '1px';
        sentinel.style.pointerEvents = 'none';
        sentinel.setAttribute('data-header-sentinel', '');
        document.body.prepend(sentinel);

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry) {
                    setIsSticky(!entry.isIntersecting);
                }
            },
            { threshold: 0 }
        );

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
            sentinel.remove();
        };
    }, []);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!(e.target as HTMLElement).closest('.account-dropdown')) {
                setAccountMenuOpen(false);
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    const toggleSearch = () => {
        setSearchExpanded(!searchExpanded);
        if (!searchExpanded) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    };

    const handleSearch = (e: React.FormEvent | React.KeyboardEvent) => {
        if ('key' in e && e.key !== 'Enter') return;
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
            setSearchExpanded(false);
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        setAccountMenuOpen(false);
        if (mobileMenuOpen) dispatch(setMobileMenuOpen(false));
        navigate('/');
    };

    const toggleMobileMenu = () => {
        dispatch(setMobileMenuOpen(!mobileMenuOpen));
    };

    const { data: settings } = useSiteSettings();

    const rawNavLinks = settings?.headerLinks || [
        { href: '/', label: 'HOME' },
        { href: '/shop', label: 'SHOP' },
        { href: '/brands', label: 'BRANDS' },
        { href: '/about-us', label: 'ABOUT US' },
        { href: '/contact-us', label: 'CONTACT US' },
    ];

    const navLinks = rawNavLinks
        .filter((link: any) => link.href !== '/bespoke-services')
        .map((link: any) => {
            if (link.href === '/brands' || link.label === 'BRANDS') {
                return { href: '/categories', label: 'CATEGORIES' };
            }
            return link;
        });

    const getLogoUrl = (path: string) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${import.meta.env.VITE_API_BASE_URL.replace(/(\/api)(?=\/api$|$)/, '')}${path}`;
    };

    return (
        <nav className={cn("BenavidezBoxing-navbar", isSticky && "sticky-nav")}>
            <div className="nav-container">
                {/* Left: Logo & Toggle */}
                <div className="nav-left flex items-center gap-4 lg:gap-8">
                    <Link to="/" className="BenavidezBoxing-logo shrink-0">
                        {settings?.logoUrl ? (
                            <img
                                src={getLogoUrl(settings.logoUrl)}
                                alt={import.meta.env.VITE_APP_NAME}
                                className="BenavidezBoxing-logo-img"
                            />
                        ) : (
                            <span className="main-brand text-2xl md:text-3xl">BenavidezBoxing</span>
                        )}
                    </Link>
                    <div className="menu-toggle !hidden md:!flex items-center gap-2 cursor-pointer group" onClick={toggleMobileMenu}>
                        <span className="material-symbols-outlined group-hover:text-[#C4A661] transition-colors">menu</span>
                        <span className="toggle-text text-[11px] font-bold tracking-widest group-hover:text-[#C4A661] transition-colors">CATEGORIES & COLLECTIONS</span>
                    </div>
                    {/* Mobile Menu Icon Only */}
                    <button className="md:!hidden flex items-center justify-center w-10 h-10" onClick={toggleMobileMenu}>
                        <span className="material-symbols-outlined text-2xl">menu</span>
                    </button>
                </div>

                {/* Center: Navigation Links */}
                <div className="nav-center !hidden lg:!flex items-center justify-center gap-8">
                    {navLinks.map((link: any) => {
                        const isShop = link.href === '/shop' || link.label === 'SHOP';
                        if (isShop) {
                            const gridColsClass =
                                activeCategories.length === 1 ? 'grid-cols-1' :
                                    activeCategories.length === 2 ? 'grid-cols-2' :
                                        activeCategories.length === 3 ? 'grid-cols-3' :
                                            activeCategories.length === 4 ? 'grid-cols-4' :
                                                'grid-cols-5';

                            const widthClass =
                                activeCategories.length === 1 ? 'cols-1' :
                                    activeCategories.length === 2 ? 'cols-2' :
                                        activeCategories.length === 3 ? 'cols-3' :
                                            activeCategories.length === 4 ? 'cols-4' :
                                                'cols-5';

                            return (
                                <div
                                    key={link.href}
                                    className="nav-item-shop-container"
                                    onMouseEnter={() => setShopDropdownOpen(true)}
                                    onMouseLeave={() => setShopDropdownOpen(false)}
                                >
                                    <div className="flex items-center gap-1">
                                        <Link
                                            to="/shop"
                                            onClick={() => setShopDropdownOpen(false)}
                                            className={cn(
                                                "text-[11px] font-bold tracking-[0.2em] transition-all hover:text-[#C4A661]",
                                                (location.pathname === '/shop' || location.pathname.startsWith('/shop')) && "text-[#C4A661] active"
                                            )}
                                        >
                                            {link.label}
                                        </Link>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setShopDropdownOpen(!shopDropdownOpen);
                                            }}
                                            className="dropdown-arrow-btn"
                                            aria-label="Toggle Shop Dropdown"
                                        >
                                            <span
                                                className={cn(
                                                    "material-symbols-outlined !text-[18px] transition-transform duration-200",
                                                    shopDropdownOpen && "rotate-180"
                                                )}
                                            >
                                                keyboard_arrow_down
                                            </span>
                                        </button>
                                    </div>

                                    {shopDropdownOpen && activeCategories.length > 0 && (
                                        <div className={cn("shop-mega-menu shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200", widthClass)}>
                                            <div className={cn("shop-mega-menu-grid", gridColsClass)}>
                                                {activeCategories.map((category) => {
                                                    const subs = groupedSubCategories[category.id] || [];
                                                    return (
                                                        <div key={category.id} className="shop-mega-menu-column">
                                                            <Link
                                                                to={`/shop?category=${category.id}`}
                                                                onClick={() => setShopDropdownOpen(false)}
                                                                className="shop-mega-menu-category-title"
                                                            >
                                                                {category.name}
                                                            </Link>
                                                            <div className="shop-mega-menu-subcategory-list">
                                                                {subs.map((sub) => (
                                                                    <Link
                                                                        key={sub.id}
                                                                        to={`/shop?category=${category.id}&subCategory=${sub.id}`}
                                                                        onClick={() => setShopDropdownOpen(false)}
                                                                        className="shop-mega-menu-subcategory-link"
                                                                    >
                                                                        {sub.name}
                                                                    </Link>
                                                                ))}
                                                                <Link
                                                                    to={`/shop?category=${category.id}`}
                                                                    onClick={() => setShopDropdownOpen(false)}
                                                                    className="shop-mega-menu-view-all-link"
                                                                >
                                                                    View All
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {/* <div className="shop-mega-menu-footer">
                                                <div className="shop-mega-menu-footer-item">
                                                    <span className="material-symbols-outlined !text-[18px]">local_shipping</span>
                                                    <span>Free Shipping Over $150</span>
                                                </div>
                                                <div className="shop-mega-menu-footer-item">
                                                    <span className="material-symbols-outlined !text-[18px]">verified_user</span>
                                                    <span>Secure Checkout</span>
                                                </div>
                                                <div className="shop-mega-menu-footer-item">
                                                    <span className="material-symbols-outlined !text-[18px]">workspace_premium</span>
                                                    <span>Premium Quality Equipment</span>
                                                </div> 
                                            </div> */}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <Link
                                key={link.href}
                                to={link.href}
                                className={cn(
                                    "text-[11px] font-bold tracking-[0.2em] transition-all hover:text-[#C4A661]",
                                    location.pathname === link.href && "text-[#C4A661] active"
                                )}
                            >
                                {link.label}
                            </Link>
                        );
                    })}
                </div>

                {/* Right: Icons */}
                <div className="nav-right flex items-center shrink-0">
                    {/* Search Input */}
                    <div className={cn("nav-search flex items-center", searchExpanded && "expanded")}>
                        <div className={cn("overflow-hidden transition-all duration-300 flex items-center", searchExpanded ? "w-[180px] md:w-[250px] opacity-100" : "w-0 opacity-0")}>
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search..."
                                className="bg-transparent border-b border-gray-200 focus:border-[#C4A661] outline-none text-sm w-full py-1"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleSearch}
                            />
                        </div>
                        <button className="icon-btn search-btn p-2" onClick={toggleSearch}>
                            <span className="material-symbols-outlined text-[24px]">{searchExpanded ? 'close' : 'search'}</span>
                        </button>
                    </div>

                    {/* Hide other icons on mobile when search is expanded to prevent overflow */}
                    <div className={cn("flex items-center transition-all duration-200", searchExpanded ? "hidden xs:flex opacity-0 xs:opacity-100" : "flex opacity-100")}>
                        {/* Cart Bag */}
                        <button className="icon-btn bag-btn relative p-2" onClick={() => dispatch(setCartOpen(true))}>
                            <span className="material-symbols-outlined text-[26px]">shopping_bag</span>
                            {totalItems > 0 && (
                                <span className="absolute top-1 right-1 bg-[#C4A661] text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-sm">
                                    {totalItems}
                                </span>
                            )}
                        </button>

                        {/* Account/Login */}
                        <div className="account-dropdown relative ml-1 md:ml-2">
                            {isUserAuthenticated ? (
                                <>
                                    <button className="icon-btn account-btn p-2" onClick={(e) => { e.stopPropagation(); setAccountMenuOpen(!accountMenuOpen); }}>
                                        <span className="material-symbols-outlined text-[26px]">account_circle</span>
                                    </button>
                                    {accountMenuOpen && (
                                        <div className="account-menu">
                                            <div className="account-menu-header">
                                                <span className="user-name">{user?.firstName} {user?.lastName}</span>
                                                <span className="welcome-text">Welcome back!</span>
                                            </div>
                                            <div className="account-menu-links">
                                                <Link to="/my-account" onClick={() => setAccountMenuOpen(false)}>
                                                    <span className="material-symbols-outlined">person</span>
                                                    My Account
                                                </Link>
                                                <button className="logout-btn" onClick={handleLogout}>
                                                    <span className="material-symbols-outlined">logout</span>
                                                    Logout
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Link to="/authentication" className="login-btn flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 bg-gray-50 hover:bg-gray-100 border border-gray-100 rounded-md transition-all">
                                    <span className="material-symbols-outlined text-[20px]">person</span>
                                    <span className="login-text text-[11px] font-bold tracking-widest hidden sm:inline">LOGIN</span>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile/Sidebar Overlay */}
            {mobileMenuOpen && (
                <div className="BenavidezBoxing-mobile-overlay">
                    <button
                        className="close-overlay icon-btn"
                        onClick={() => dispatch(setMobileMenuOpen(false))}
                    >
                        <span className="material-symbols-outlined !text-[32px]">close</span>
                    </button>

                    <div className="overlay-content py-10 w-full">
                        <div className="flex flex-col items-center gap-6 w-full max-h-[60vh] overflow-y-auto px-4 no-scrollbar">
                            {navLinks.map((link: any) => {
                                const isShop = link.href === '/shop' || link.label === 'SHOP';
                                if (isShop) {
                                    return (
                                        <div key={link.href} className="w-full flex flex-col items-center">
                                            <div className="flex items-center gap-2 text-2xl font-serif-lux">
                                                <Link
                                                    to="/shop"
                                                    onClick={() => dispatch(setMobileMenuOpen(false))}
                                                    className="font-serif-lux text-2xl"
                                                >
                                                    {link.label}
                                                </Link>
                                                <button
                                                    onClick={() => setMobileShopOpen(!mobileShopOpen)}
                                                    className="flex items-center justify-center p-1 text-gray-500 hover:text-[#C4A661]"
                                                    aria-label="Toggle Shop categories accordion"
                                                >
                                                    <span className={cn(
                                                        "material-symbols-outlined !text-[24px] transition-transform duration-200",
                                                        mobileShopOpen && "rotate-180"
                                                    )}>
                                                        keyboard_arrow_down
                                                    </span>
                                                </button>
                                            </div>

                                            {mobileShopOpen && activeCategories.length > 0 && (
                                                <div className="w-full max-w-[280px] mt-4 flex flex-col gap-4 pl-4 border-l border-gray-100 items-start animate-in fade-in slide-in-from-top-2 duration-200">
                                                    {activeCategories.map((category) => {
                                                        const isCategoryExpanded = mobileCategoryOpen === category.id;
                                                        const subs = groupedSubCategories[category.id] || [];

                                                        return (
                                                            <div key={category.id} className="w-full flex flex-col items-start">
                                                                <div className="w-full flex items-center justify-between gap-2">
                                                                    <Link
                                                                        to={`/shop?category=${category.id}`}
                                                                        onClick={() => dispatch(setMobileMenuOpen(false))}
                                                                        className="text-lg font-serif text-gray-800 hover:text-[#C4A661] text-left uppercase tracking-wider"
                                                                    >
                                                                        {category.name}
                                                                    </Link>
                                                                    {subs.length > 0 && (
                                                                        <button
                                                                            onClick={() => setMobileCategoryOpen(isCategoryExpanded ? null : category.id)}
                                                                            className="flex items-center justify-center p-1 text-gray-400"
                                                                            aria-label={`Toggle subcategories for ${category.name}`}
                                                                        >
                                                                            <span className={cn(
                                                                                "material-symbols-outlined !text-[20px] transition-transform duration-200",
                                                                                isCategoryExpanded && "rotate-180"
                                                                            )}>
                                                                                keyboard_arrow_down
                                                                            </span>
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                {isCategoryExpanded && subs.length > 0 && (
                                                                    <div className="w-full mt-2 pl-4 flex flex-col gap-2.5 items-start animate-in fade-in slide-in-from-top-1 duration-150">
                                                                        {subs.map((sub) => (
                                                                            <Link
                                                                                key={sub.id}
                                                                                to={`/shop?category=${category.id}&subCategory=${sub.id}`}
                                                                                onClick={() => dispatch(setMobileMenuOpen(false))}
                                                                                className="text-md font-sans text-gray-500 hover:text-[#C4A661]"
                                                                            >
                                                                                {sub.name}
                                                                            </Link>
                                                                        ))}
                                                                        <Link
                                                                            to={`/shop?category=${category.id}`}
                                                                            onClick={() => dispatch(setMobileMenuOpen(false))}
                                                                            className="text-md font-bold text-gray-700 hover:text-[#C4A661] uppercase tracking-wider"
                                                                        >
                                                                            View All
                                                                        </Link>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <Link
                                        key={link.href}
                                        to={link.href}
                                        className="text-2xl font-serif-lux"
                                        onClick={() => dispatch(setMobileMenuOpen(false))}
                                    >
                                        {link.label}
                                    </Link>
                                );
                            })}
                        </div>

                        <hr className="my-8 mx-auto w-1/4 opacity-30" />

                        <div className="flex flex-col items-center gap-6">
                            {isUserAuthenticated ? (
                                <>
                                    <Link
                                        to="/my-account"
                                        className="text-xl font-sans-lux"
                                        onClick={() => dispatch(setMobileMenuOpen(false))}
                                    >
                                        MY ACCOUNT
                                    </Link>
                                    <button
                                        className="text-xl font-sans-lux text-red-500"
                                        onClick={handleLogout}
                                    >
                                        LOGOUT
                                    </button>
                                </>
                            ) : (
                                <Link
                                    to="/authentication"
                                    className="text-xl font-sans-lux"
                                    onClick={() => dispatch(setMobileMenuOpen(false))}
                                >
                                    LOGIN / REGISTER
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
}

export default Header;
